import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-1 md:gap-4">
    <span className="text-gray-500">{label}</span>
    <span
      className={`font-medium text-gray-800 text-left md:text-right break-all ${
        mono ? 'font-mono tracking-wider' : ''
      }`}
    >
      {value}
    </span>
  </div>
);

const CopyButton = ({ text, children }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {}
      }}
      className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50"
    >
      <Copy className="w-4 h-4" />
      {copied ? 'Copied!' : children}
    </button>
  );
};

const GiftCard = ({ title, holder, iban, bic, paypalEmail, paypalUsername, reference }) => {
  const { t } = useTranslation();
  const isPayPal = !!paypalEmail;
  
  const fullDetails = isPayPal 
    ? `${t('gift.paypalEmail')}: ${paypalEmail}
${t('gift.paypalUsername')}: ${paypalUsername}
${t('gift.reference')}: ${reference}`
    : `${t('gift.accountHolder')}: ${holder}
${t('gift.iban')}: ${iban}
${t('gift.bic')}: ${bic}
${t('gift.reference')}: ${reference}`;

  return (
    <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
      <h4 className="font-semibold text-gray-800">{title}</h4>

      <div className="space-y-3">
        {isPayPal ? (
          <>
            <InfoRow label={t('gift.paypalEmail')} value={paypalEmail} mono />
            <InfoRow label={t('gift.paypalUsername')} value={paypalUsername} mono />
            <InfoRow label={t('gift.reference')} value={reference} />
          </>
        ) : (
          <>
            <InfoRow label={t('gift.accountHolder')} value={holder} />
            <InfoRow label={t('gift.iban')} value={iban} mono />
            <InfoRow label={t('gift.bic')} value={bic} mono />
            <InfoRow label={t('gift.reference')} value={reference} />
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {isPayPal ? (
          <CopyButton text={paypalEmail}>{t('gift.copyEmail')}</CopyButton>
        ) : (
          <CopyButton text={iban}>{t('gift.copyIban')}</CopyButton>
        )}
        <CopyButton text={fullDetails}>{t('gift.copyAll')}</CopyButton>
      </div>
    </div>
  );
};

const GiftBankDetails = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <p className="text-gray-600">{t('gift.intro')}</p>

      {/* stacked cards, one under the other */}
      <div className="space-y-4">
        <GiftCard
          title={t('gift.germanyTitle')}
          holder="Vasiliki Ioannidou"                 // <- your German account holder
          iban="DE87 1203 0000 1063 9741 07"         // <- real IBAN (spaced)
          bic="BYLADEM1001"                          // <- real BIC
          reference={t('gift.referenceValue')}       // e.g. “Wedding gift – Your Name”
        />

        {/* subtle divider between the two (optional) */}
        <div className="border-t border-gray-100" />

        <GiftCard
          title={t('gift.greeceTitle')}
          holder="Βασιλική Ιωαννίδου"                // <- Greek account holder
          iban="GR44 0171 2580 0062 5815 4586 074"   // <- real IBAN (spaced)
          bic="PIRBGRAA"                           // <- real BIC
          reference={t('gift.referenceValue')}
        />

        {/* subtle divider */}
        <div className="border-t border-gray-100" />

        <GiftCard
          title={t('gift.paypalTitle')}
          paypalEmail="contactveenco@gmail.com"  // <- Replace with your actual PayPal email
          paypalUsername="@veenco"  // <- Replace with your actual PayPal username
          reference={t('gift.referenceValue')}
        />
      </div>

      <p className="text-sm text-gray-500">{t('gift.privacyNote')}</p>
    </div>
  );
};

export default GiftBankDetails;